export function deepPatch<T>(source: T, target: T): T {
    if (source === target) return source;

    // If primitive or null
    if (source === null || target === null || typeof source !== 'object' || typeof target !== 'object') {
        return source === target ? source : target;
    }

    // If arrays
    if (Array.isArray(source) && Array.isArray(target)) {
        return deepPatchArray(source, target);
    }

    // If one is array and other is not
    if (Array.isArray(source) || Array.isArray(target)) {
        return target;
    }

    // Maps
    if (source instanceof Map && target instanceof Map) {
        return deepPatchMap(source, target) as T;
    }

    if (source instanceof Map || target instanceof Map) {
        return target;
    }

    // Sets
    if (source instanceof Set && target instanceof Set) {
        return deepPatchSet(source, target) as T;
    }

    if (source instanceof Set || target instanceof Set) {
        return target;
    }

    // For objects: recursively patch each field
    const result: any = {};

    const sourceKeys = Object.keys(source as any);
    const targetKeys = Object.keys(target as any);

    let hasChanges = sourceKeys.length !== targetKeys.length;

    if (targetKeys.length === 0) {
        // It might be an object with an internal state e.g., Date, RegExp, Map, Set, etc.
        return target;
    }

    for (const key of targetKeys) {
        const sourceVal = (source as any)[key];
        const targetVal = (target as any)[key];
        const patchedVal = deepPatch(sourceVal, targetVal);

        result[key] = patchedVal;

        if (patchedVal !== sourceVal) {
            hasChanges = true;
        }
    }

    // Set result prototype to target prototype
    Object.setPrototypeOf(result, Object.getPrototypeOf(target));

    // If no changes, return source to preserve reference
    return hasChanges ? result : source;
}

function deepPatchArray<T extends Array<any>>(source: T, target: T): T {
    // Special case: arrays of primitives
    if (target.every(item => item === null || typeof item !== 'object')) {
        // Check if arrays are equal
        if (source.length === target.length && source.every((item, i) => item === target[i])) {
            return source;
        }
        return target;
    }

    // Special case: arrays of objects with 'id' keys
    if (target.every(item => item !== null && typeof item === 'object' && 'id' in item) &&
        source.every(item => item !== null && typeof item === 'object' && 'id' in item)) {

        // Build a map of source elements by id
        const sourceById = new Map();
        for (const item of source) {
            sourceById.set(item.id, item);
        }

        // Patch each target element
        const result: any[] = [];
        let hasChanges = false;

        for (const targetItem of target) {
            const sourceItem = sourceById.get(targetItem.id);
            if (sourceItem !== undefined) {
                const patchedItem = deepPatch(sourceItem, targetItem);
                result.push(patchedItem);
                if (patchedItem !== sourceItem) {
                    hasChanges = true;
                }
            } else {
                // New item in target, not in source
                result.push(targetItem);
                hasChanges = true;
            }
        }

        // Check if array length changed
        if (source.length !== target.length) {
            hasChanges = true;
        }

        return hasChanges ? result as T : source;
    }

    // General case: return target for now
    return target;
}

function deepPatchMap<K, V>(source: Map<K, V>, target: Map<K, V>): Map<K, V> {
    let hasChanges = source.size !== target.size;
    const result = new Map<K, V>();

    for (const [key, targetValue] of target.entries()) {
        if (source.has(key)) {
            const sourceValue = source.get(key) as V;
            const patchedValue = deepPatch(sourceValue, targetValue);
            result.set(key, patchedValue);
            if (patchedValue !== sourceValue) {
                hasChanges = true;
            }
        } else {
            result.set(key, targetValue);
            hasChanges = true;
        }
    }

    return hasChanges ? result : source;
}

function deepPatchSet<T>(source: Set<T>, target: Set<T>): Set<T> {
    if (source.size !== target.size) {
        return target;
    }

    for (const value of target.values()) {
        if (!source.has(value)) {
            return target;
        }
    }

    return source;
}
